import { useEffect, useState, useRef } from 'react';
import { Button, Icon, Table, Input, Loader, Modal } from '@scuf/common';
import { useSetRecoilState } from 'recoil';
import { USER_MANAGER } from 'resources/links';
import useUser from 'helpers/hooks/useUser';
import useFetch from 'helpers/hooks/useFetch';
import { notificationAtom } from 'resources';
import axios from 'axios';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

interface ManualFile {
    id: number;
    displayName: string;
    storedFileName: string;
    section: string;
}

interface FileContentProps {
    section: string;
}

function FileContent({ section }: FileContentProps): JSX.Element {
    const { t } = useTranslation();
    const [files, setFiles] = useState<ManualFile[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [customDisplayName, setCustomDisplayName] = useState<string>('');
    const [showModal, setShowModal] = useState<boolean>(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { user } = useUser();
    const setNotification = useSetRecoilState(notificationAtom);
    //const [cookie] = useCookies(['inf-o']);
    const isEditable = user?.role === '1' || user?.role === '2';

    const [downloadingId, setDownloadingId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const [, fetchFiles] = useFetch<void, ManualFile[]>({
        url: `${USER_MANAGER.getManualFilesBySection}?section=${section}`,
        method: 'GET',
        onError: t('manual.fetchFilesError')
    });

    const [, deleteFile] = useFetch<{ id: number }>({
        url: USER_MANAGER.deleteManualFile,
        method: 'DELETE',
        onError: t('manual.deleteFileError')
    });

    useEffect(() => {
        loadFiles();
    }, [section]);

    const loadFiles = async () => {
        setLoading(true);
        await fetchFiles(undefined, resp => {
            setFiles(resp?.data ?? []);
            setLoading(false);
        });
    };

    const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !customDisplayName.trim()) return;

        const formData = new FormData();
        formData.append('formFile', file);
        formData.append('section', section);
        formData.append('displayName', customDisplayName.trim());
        formData.append('ssoId', user?.ssoid ?? '');

        try {
            setLoading(true);
            const response = await axios.post(USER_MANAGER.uploadManualFile, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    //Authorization: `Bearer ${cookie.info}`
                },
                withCredentials: true
            });

            const savedFile = response.data?.data as ManualFile;

            if (savedFile) {
                setFiles(prev => [...prev, savedFile]);
                setNotification(old => ({
                    ...old,
                    message: t('manual.uploadSuccess'),
                    type: 'success'
                }));
                setCustomDisplayName('');
                setShowModal(false);
            }
        } catch {
            setNotification(old => ({
                ...old,
                message: t('manual.uploadError'),
                type: 'critical'
            }));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        setDeletingId(id);
        try {
            await deleteFile({ id });
            setFiles(prev => prev.filter(f => f.id !== id));
            setNotification(old => ({
                ...old,
                message: t('manual.deleteSuccess'),
                type: 'success'
            }));
        } catch {
            setNotification(old => ({
                ...old,
                message: t('manual.deleteError'),
                type: 'critical'
            }));
        } finally {
            setDeletingId(null);
        }
    };

    const handleDownload = async (id: number, displayName: string, storedFileName: string) => {
        try {
            setDownloadingId(id);
            const response = await fetch(`${USER_MANAGER.downloadManualFileById}?id=${id}`, {
                method: 'GET',
                headers: {
                    Accept: 'application/octet-stream',
                    //Authorization: `Bearer ${cookie.info}`
                },
                credentials: 'include'
            });

            const blob = await response.blob();
            const extension = storedFileName.includes('.') ? storedFileName.slice(storedFileName.lastIndexOf('.')) : '';
            const finalName = displayName.endsWith(extension) ? displayName : `${displayName}${extension}`;

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', finalName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            setNotification(old => ({
                ...old,
                message: t('manual.downloadError'),
                type: 'critical'
            }));
        } finally {
            setDownloadingId(null);
        }
    };

    const openModal = () => {
        setCustomDisplayName('');
        setShowModal(true);
    };

    const confirmDisplayName = () => {
        if (!customDisplayName.trim()) {
            setNotification(old => ({
                ...old,
                message: t('manual.enterFileName'),
                type: 'information'
            }));
            return;
        }
        setShowModal(false);
        fileInputRef.current?.click();
    };

    return (
        <div>
            {isEditable && (
                <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'end', paddingBottom: '10px' }}>
                    <Button type="primary" onClick={openModal} className="upload-button">
                        {t('manual.upload')}
                    </Button>
                    <input type="file" ref={fileInputRef} onChange={handleUpload} style={{ display: 'none' }} />
                </div>
            )}

            {loading ? (
                <Loader text={t('manual.loading')} />
            ) : (
                <Table>
                    <Table.Header>
                        <Table.HeaderCell content={t('manual.fileName')} />
                        <Table.HeaderCell />
                    </Table.Header>
                    <Table.Body>
                        {files.map(file => (
                            <Table.Row key={file.id}>
                                <Table.Cell content={file.displayName} />
                                <Table.Cell>
                                    <div style={{ display: 'flex', gap: '4rem', justifyContent: 'end', cursor: 'pointer' }}>
                                        {downloadingId === file.id ? (
                                            <Icon name="refresh" loading exactSize={25} />
                                        ) : (
                                            <Icon
                                                name="file-download"
                                                exactSize={25}
                                                color="gray"
                                                onClick={() => handleDownload(file.id, file.displayName, file.storedFileName)}
                                            />
                                        )}

                                        {isEditable &&
                                            (deletingId === file.id ? (
                                                <Icon name="refresh" loading size="medium" />
                                            ) : (
                                                <Icon
                                                    name="delete"
                                                    size="medium"
                                                    color="gray"
                                                    onClick={() => handleDelete(file.id)}
                                                />
                                            ))}
                                    </div>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}

            <Modal open={showModal} onClose={() => setShowModal(false)} size="small" closeIcon>
                <Modal.Header className="modal-header">{t('manual.addFileName')}</Modal.Header>
                <Modal.Content>
                    <Input
                        className="modal-input"
                        placeholder={t('manual.typeFileName')}
                        value={customDisplayName}
                        onChange={value => setCustomDisplayName(value)}
                    />
                </Modal.Content>
                <Modal.Footer>
                    <Button type="secondary" content={t('manual.cancel')} onClick={() => setShowModal(false)} />
                    <Button type="primary" content={t('manual.confirm')} onClick={confirmDisplayName} />
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default FileContent;
